import { Flex } from '@atlaskit/primitives';

import { type FunctionComponent, useEffect } from 'react';
import Header from './Header/Header';
import Description from './Description';
import Warning from './Warning';
import { useInfo, useToken } from '../../Flow/ConsentContextProvider';
import { useUser } from '#definitions/user';
import { defineMessages, useIntl } from 'react-intl';

const AppLinks: FunctionComponent = () => {
  const {
    customer: rawAppName,
    cloudId,
    clientId,
    clientName: rawClientName,
  } = useToken();
  const { siteName } = useInfo();
  const user = useUser();
  const intl = useIntl();
  const appName = determineAppName(rawAppName);
  const clientName =
    rawClientName ??
    clientId ??
    intl.formatMessage(messages.unknownApplication);

  useEffect(() => {
    document.title = intl.formatMessage(messages.documentTitle, {
      customerName: appName,
      clientName,
    });
  }, [appName, clientName, intl]);

  return (
    <Flex gap="space.400" direction="column" wrap="wrap" alignItems="start">
      <Flex alignItems="center" direction="column" gap="space.400">
        <Header appName={appName} />
        <Description
          url={siteName ?? `<${cloudId}>`}
          userFullName={user?.name || 'null'}
          clientName={clientName}
        />
        <Warning clientName={clientName} appName={appName} />
      </Flex>
    </Flex>
  );
};

function determineAppName(rawAppName: string | null | undefined): string {
  switch (rawAppName) {
    case 'jira':
      return 'Jira';
    case 'confluence':
      return 'Confluence';
    default:
      return 'Unknown';
  }
}

const messages = defineMessages({
  documentTitle: {
    id: 'applinks.documentTitle',
    defaultMessage: 'Welcome to {customerName} - {clientName}',
    description:
      'The document title that acts as the tab name. {customerName} is either Confluence or Jira.',
  },
  unknownApplication: {
    id: 'applinks.unknownApplication',
    defaultMessage: 'An unknown application',
    description:
      'Fallback shown in place of the requesting application name when the consent token carries neither a client name nor a client id',
  },
});

export default AppLinks;
